import useRequest from "@/lib/request/useRequest";
import { FeatureCollection, MultiPolygon } from "geojson";
import { Plot, TrendlineTable } from "@/types/plot";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "@heroui/react";
import { UseQueryResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import { roundTo } from "@/lib/utils/numbers";
import { calculateYBands } from "@/lib/utils/sensitivity";
import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import {
    FillLayerSpecification,
    LineLayerSpecification,
    SymbolLayerSpecification,
} from "mapbox-gl";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { useTranslation } from "react-i18next";
import { RangeValue, DateValue } from "@heroui/react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import TableV2 from "@/components/table/TableV2";

const TABLE_SOURCE = "trendline-table-source";
const FILL_LAYER: FillLayerSpecification = {
    source: TABLE_SOURCE,
    id: "trendline-table-fill",
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    layout: {},
    paint: {
        "fill-color": "#FFF",
        "fill-opacity": 0.5,
    },
};
const LINE_LAYER: LineLayerSpecification = {
    source: TABLE_SOURCE,
    id: "trendline-table-line",
    type: "line",
    layout: {},
    paint: {
        "line-color": "#FFF",
        "line-width": 3,
    },
    minzoom: 14,
};
const SYMBOL_LAYER: SymbolLayerSpecification = {
    source: TABLE_SOURCE,
    id: "trendline-table-symbol",
    type: "symbol",
    layout: {
        "text-field": ["get", "pair_id"],
        "text-size": 12,
    },
};

type TrendlineRow = TrendlineTable & {
    index: number;
    sensitized_si: number;
    area_rep: number;
    percent_area_rep: number;
    ndre_mean: number;
};

function SITable({
    fieldId,
    trendlineQuery,
    dateRange,
    pairIds,
}: {
    fieldId: string;
    trendlineQuery: UseQueryResult<TrendlineTable[]>;
    dateRange: RangeValue<DateValue> | null;
    pairIds: string[];
}) {
    const { t } = useTranslation("insights");
    const { map, mapLoaded } = useMap();
    const { useGetRequest } = useRequest();

    const uploadedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/uploaded_plots/`,
        params: { boundary: "analytic" },
    });
    const appliedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/applied_plots/`,
        params: { boundary: "analytic" },
    });

    const plots = useMemo(() => {
        return [
            ...(uploadedPlots?.data?.features ?? []),
            ...(appliedPlots?.data?.features ?? []),
        ];
    }, [appliedPlots?.data?.features, uploadedPlots?.data?.features]);

    const dataInTimeRange = useMemo((): TrendlineRow[] => {
        return (
            trendlineQuery?.data?.filter(
                (row) =>
                    dayjs(row.capture_date).isAfter(dayjs(dateRange?.start?.toString())) &&
                    dayjs(row.capture_date).isBefore(dayjs(dateRange?.end?.toString())) &&
                    (!pairIds?.length ||
                        pairIds?.includes(row.pair_id.toString())),
            ) ?? []
        ).map((row, index) => ({
            ...row,
            index,
            sensitized_si: roundTo(row.sensitized_si * 100, 1),
            area_rep: roundTo(row.area_rep),
            percent_area_rep: roundTo(row.percent_area_rep),
            ndre_mean: roundTo(row.ndre_mean, 4),
        }));
    }, [dateRange, pairIds, trendlineQuery?.data]);

    const { fieldQuery } = useFieldDashboard() ?? {};

    const bands = useMemo(
        () =>
            calculateYBands(
                fieldQuery?.data?.insight_sensitivity ?? "moderate",
            ),
        [fieldQuery?.data?.insight_sensitivity],
    );

    useEffect(() => {
        if (!map || !mapLoaded) return;

        addUpdateGeojsonSource(map, TABLE_SOURCE, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
        });

        addUpdateGeojsonLayers(map, [FILL_LAYER, LINE_LAYER, SYMBOL_LAYER]);

        return () => {
            if (map && mapLoaded) {
                removeLayersIfExists(map, [
                    FILL_LAYER.id,
                    LINE_LAYER.id,
                    SYMBOL_LAYER.id,
                ]);
                removeSourcesIfExists(map, [TABLE_SOURCE]);
            }
        };
    }, [map, mapLoaded]);

    const onRowMouseEnter = useCallback(
        (row: TrendlineRow) => {
            if (!map || !mapLoaded) return;
            const plot = plots?.find((plot) => plot.id == row.plot_id);
            if (!plot) return;
            addUpdateGeojsonSource(map, TABLE_SOURCE, {
                type: "geojson",
                data: plot,
                tolerance: 0,
            });
        },
        [map, mapLoaded, plots],
    );

    const [sorting, setSorting] = useState<SortingState>([
        { id: "capture_date", desc: true },
        { id: "pair_id", desc: false },
    ]);

    const columns = useMemo<ColumnDef<TrendlineRow>[]>(
        () => [
            { accessorKey: "capture_date", header: t("tableColumns.date"), size: 100 },
            { accessorKey: "plot_type", header: t("tableColumns.plotType"), size: 80 },
            { accessorKey: "pair_id", header: t("tableColumns.pairId"), size: 60 },
            {
                accessorKey: "sensitized_si",
                header: t("tableColumns.siPercent"),
                size: 80,
                cell: ({ getValue }) => {
                    const si = getValue() as number;
                    const color =
                        bands.find((band) => si > band.y0 && si <= band.y1)
                            ?.color ?? "white";
                    return (
                        <div
                            style={{ backgroundColor: color }}
                            className="rounded-md font-medium p-1 text-center text-xs"
                        >
                            {si}%
                        </div>
                    );
                },
            },
            { accessorKey: "si_status", header: t("tableColumns.status"), size: 80 },
            { accessorKey: "ndre_mean", header: t("tableColumns.ndre"), size: 80 },
            { accessorKey: "area_rep", header: t("tableColumns.rangeAc"), size: 80 },
            { accessorKey: "percent_area_rep", header: t("tableColumns.weightPercent"), size: 100 },
        ],
        [t, bands],
    );

    if (!appliedPlots.isFetched || !uploadedPlots.isFetched) {
        return <Skeleton className="h-[500px] w-full rounded-md p-2" />;
    }

    return (
        <div className="h-[500px] select-none text-xs">
            <TableV2
                data={dataInTimeRange}
                columns={columns}
                sorting={sorting}
                setSorting={setSorting}
                isLoading={trendlineQuery.isFetching}
                estimateSize={40}
                onRowMouseEnter={onRowMouseEnter}
            />
        </div>
    );
}

export default SITable;
